import { useCallback, useEffect, useState } from 'react'
import { HomeSession } from '@application/session.ts'
import type { EventStore, PhotoStore, PinVault } from '@application/ports.ts'
import type { CommandContext, CommandResult, ErrorCode } from '@domain/commands.ts'
import type { Actor } from '@domain/events.ts'
import type { FamilyState } from '@domain/state.ts'
import { EMPTY_STATE } from '@domain/state.ts'
import { getRuntime } from '../runtime.ts'
import { es } from '../strings/es.ts'

export interface Home {
  readonly ready: boolean
  readonly failed: boolean
  readonly state: FamilyState
  readonly today: string
  readonly session: HomeSession | null
  /** Solo para restaurar una copia: el resto pasa por la sesión. */
  readonly store: EventStore | null
  readonly photos: PhotoStore | null
  readonly vault: PinVault | null
  readonly run: (
    command: (state: FamilyState, ctx: CommandContext) => CommandResult,
    actor: Actor,
  ) => Promise<CommandResult>
}

export function messageFor(code: ErrorCode, detail?: Record<string, string | number>): string {
  // "Quedan pasos por hacer" leaves the child hunting for which one. The domain
  // already knows the first one missing, so say it.
  if (code === 'steps-missing' && typeof detail?.first === 'string' && detail.first.length > 0) {
    return es.task.missingStep(detail.first)
  }
  return es.errors[code]
}

/**
 * Opens the local session once and keeps the projection in sync. Everything the
 * screens need arrives through here; no component talks to the store.
 */
export function useHome(): Home {
  const [session, setSession] = useState<HomeSession | null>(null)
  const [store, setStore] = useState<EventStore | null>(null)
  const [photos, setPhotos] = useState<PhotoStore | null>(null)
  const [vault, setVault] = useState<PinVault | null>(null)
  const [state, setState] = useState<FamilyState>(EMPTY_STATE)
  const [failed, setFailed] = useState(false)

  useEffect(() => {
    let cancelled = false
    void (async () => {
      try {
        const runtime = await getRuntime()
        const opened = await HomeSession.open(runtime.store, runtime.clock, runtime.random)
        if (cancelled) return
        // A day that went by without an adult granting the minutes gives the
        // coins back. It runs before the first paint so a child never sees a
        // balance that is about to change under them.
        await opened.reconcile()
        if (cancelled) return
        setSession(opened)
        setStore(runtime.store)
        setPhotos(runtime.photos)
        setVault(runtime.vault)
        setState(opened.state())
        opened.subscribe(() => {
          setState(opened.state())
        })
      } catch (cause) {
        console.error('No se pudo abrir la casa', cause)
        if (!cancelled) setFailed(true)
      }
    })()
    return () => {
      cancelled = true
    }
  }, [])

  /**
   * La única puerta por la que un toque llega al registro, y la que garantiza
   * que nunca rechaza.
   *
   * Doce sitios de la app llaman a esto con `void run(...)` porque no tienen
   * nada que decir si sale bien. Si `dispatch` rechazara —disco lleno, base
   * cerrada, almacenamiento desalojado por el navegador— esas doce llamadas
   * dejarían una promesa rechazada sin dueño: en las pruebas sale como error
   * suelto, y en una tablet de verdad no sale en ninguna parte.
   *
   * Así que el fallo se convierte aquí en un resultado. Quien lo espera recibe
   * un código que puede enseñar en castellano; quien no lo espera, nada, que es
   * lo que pidió.
   */
  const run = useCallback<Home['run']>(
    async (command, actor) => {
      if (!session) return { ok: false, error: { code: 'not-configured' } }
      try {
        return await session.dispatch(command, actor)
      } catch (cause) {
        console.error('No se pudo guardar', cause)
        return { ok: false, error: { code: 'not-configured' } }
      }
    },
    [session],
  )

  // Computed on every render rather than remembered: the logical day can change
  // while the tablet sits on the kitchen counter, and it costs nothing to ask.
  const today = session?.today() ?? ''

  return {
    ready: session !== null,
    failed,
    state,
    today,
    session,
    store,
    photos,
    vault,
    run,
  }
}
